import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Style/AssessmentEC.css';
import signout_n from './Image/signout_n.png';
import signout_d from './Image/signout_d.png';
import skip_n from './Image/skip_n.png';
import skip_d from './Image/skip_d.png';
import ok_n from './Image/ok_n.png';
import ok_d from './Image/ok_d.png';
import ok_blue_n from './Image/ok_blue_n.gif';
import ok_dblue_o from './Image/ok_dblue_o.gif';
import ok_dblue_d from './Image/ok_dblue_d.gif';
import exit_white_n from './Image/exit_white_n.gif';
import exit_white_o from './Image/exit_white_o.gif';
import exit_white_d from './Image/exit_white_d.gif';
import top_blue from './Image/top_blue_panel.png';
import Loading from '../../Common/Loading';
import BQns from '../../Common/Tutorials/BQns';
import OQns from '../../Common/Tutorials/OQns';
import RadioQns from '../../Common/Tutorials/RadioQns';
import ImageQns from '../../Common/Tutorials/ImageQns';
import CQns from '../../Common/Tutorials/CQns';
import MQns from '../../Common/Tutorials/MQns';
import WQns from '../../Common/Tutorials/WQns';
import IQns from '../../Common/Tutorials/IQns';
import FQns from '../../Common/Tutorials/FQns';
import LQns from '../../Common/Tutorials/LQns';
import DQns from '../../Common/Tutorials/DQns';
import NQns from '../../Common/Tutorials/NQns';
import { getQuestionTypeFromHtml } from '../../Common/Tutorials/utils/questionTypeUtils';


interface QuestionData {
  complete: boolean;
  progress: number;
  strand: number;
  sub_strand: number;
  year: number;
  topic: number;
  question_no: number;
  question_type: string;
  question_html: string;
  question_content: string;
  correct_answer: string | null;
  options: any[];
  blanks_count: number;
  blob_assets_path: string;
  lesson_code?: string;
}

interface AssessmentData {
  success: boolean;
  first_name: string;
  ec_curriculum: string;
  study_year: number;
  lowest_year: number;
  highest_year: number;
  question_data: QuestionData;
}

interface SessionData {
  school_code: string;
  family_code: string;
  login: string;
  report_no: string;
}

const adjustLOptionsPadding = (html: string) => {
  if (!html || typeof DOMParser === 'undefined') return html;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const optionCells = doc.querySelectorAll('td[name="0"]');
    optionCells.forEach((cell) => {
      cell.setAttribute('align', 'right');
      const existingStyle = cell.getAttribute('style') || '';
      const finalStyle = `${existingStyle}${existingStyle ? '; ' : ''}padding-right: 20px !important; text-align: right !important;`;
      cell.setAttribute('style', finalStyle);
    });

    return doc.documentElement.outerHTML || html;
  } catch {
    return html;
  }
};


type AssessmentAnswerData = {
  marks: number;
  noOfOptions: number;
  enteredCorrectAnswer?: string | null;
  enteredWrongAnswer?: string | null;
  enteredBlankAnswer?: string | null;
  scoringMethod?: number | null;
  optOrder?: string;
};

const hasRecordedAssessmentAnswer = (
  answerData: AssessmentAnswerData | null | undefined
): boolean => {
  if (!answerData) return false;

  return [
    answerData.enteredCorrectAnswer,
    answerData.enteredWrongAnswer,
    answerData.enteredBlankAnswer,
  ].some((value) => String(value ?? '').trim().length > 0);
};

const normalizeAssessmentValue = (value: unknown): string =>
  String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Return the actual documents used by the currently displayed question.
 * EC question HTML is normally rendered inside an iframe, but keeping the
 * parent document here also covers question types rendered directly in DOM.
 */
const getAssessmentQuestionDocuments = (): Document[] => {
  const documents: Document[] = [];
  const container = document.getElementById('divMDFrm');
  if (!container) return documents;

  documents.push(document);

  container.querySelectorAll('iframe').forEach((iframe) => {
    try {
      if (iframe.contentDocument) documents.push(iframe.contentDocument);
    } catch {
      // Cross-origin iframe: the question component remains the source of truth.
    }
  });

  return documents;
};

/**
 * Read only genuine student-entered/selected values from a question document.
 * Hidden answer keys and placeholder selects are never counted as answers.
 */
const readRenderedAssessmentAnswer = (
  doc: Document
): { hasAnswer: boolean; values: string[]; orderValues: string[] } => {
  const values: string[] = [];
  const orderValues: string[] = [];

  // Radio / checkbox based question types.
  const checkedControls = Array.from(
    doc.querySelectorAll<HTMLInputElement>(
      'input[type="radio"]:checked, input[type="checkbox"]:checked'
    )
  );

  checkedControls.forEach((control) => {
    const value = normalizeAssessmentValue(
      control.getAttribute('name') || control.value
    );
    if (value) values.push(value);
  });

  // Dropdown / select based question types.
  const selects = Array.from(doc.querySelectorAll<HTMLSelectElement>('select'));
  selects.forEach((select) => {
    const value = normalizeAssessmentValue(select.value);
    // The assessment D-type uses an empty -- Select -- placeholder.
    if (value) {
      values.push(value);
      orderValues.push(value);
    }
  });

  // Text / numeric / fill-in / carry inputs. Ignore hidden infrastructure
  // fields such as answer-key, correct-answers, KSD_Ans, etc.
  const textControls = Array.from(
    doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="radio"]):not([type="checkbox"]), textarea'
    )
  );

  textControls.forEach((control) => {
    const value = normalizeAssessmentValue(control.value);
    if (!value) return;

    const id = (control.id || '').toLowerCase();
    const name = (control.getAttribute('name') || '').toLowerCase();

    if (
      id.includes('answer-key') ||
      id.includes('correct-answer') ||
      id.includes('correct_answers') ||
      id.includes('initial-order') ||
      id === 'ksd_ans' ||
      id === 'ksd_mw_info' ||
      id === 'ksd_bw' ||
      name === 'answer-key'
    ) {
      return;
    }

    values.push(value);
    orderValues.push(value);
  });

  // L-type uses draggable TD2_* cells rather than ordinary form controls.
  // Reading their current DOM order gives us the student's actual answer.
  const lItems = Array.from(doc.querySelectorAll<HTMLElement>('td[id^="TD2_"]'));
  if (lItems.length > 0) {
    const lValues = lItems
      .map((item) => normalizeAssessmentValue(item.textContent))
      .filter(Boolean);
    if (lValues.length > 0) {
      orderValues.splice(0, orderValues.length, ...lValues);
      values.push(...lValues);
    }
  }

  // I/custom click questions can use contenteditable or aria-selected state.
  const editable = Array.from(doc.querySelectorAll<HTMLElement>('[contenteditable="true"]'));
  editable.forEach((element) => {
    const value = normalizeAssessmentValue(element.textContent);
    if (value) values.push(value);
  });

  const selectedCustom = Array.from(
    doc.querySelectorAll<HTMLElement>('[aria-checked="true"], [aria-selected="true"]')
  );
  selectedCustom.forEach((element) => {
    const value = normalizeAssessmentValue(
      element.getAttribute('data-value') || element.getAttribute('data-id') || element.textContent
    );
    if (value) values.push(value);
  });

  // De-duplicate without changing the student's order.
  const uniqueValues = Array.from(new Set(values));

  return {
    hasAnswer: uniqueValues.length > 0,
    values: uniqueValues,
    orderValues: orderValues.length > 0 ? orderValues : uniqueValues,
  };
};

/**
 * Synchronise legacy iframe controls before retrying the component's own
 * getAnswer(). This does not alter values; it only fires the events that the
 * existing question components normally receive from user interaction.
 */
const hasRenderedAssessmentAnswer = (): boolean =>
  getAssessmentQuestionDocuments().some(
    (doc) => readRenderedAssessmentAnswer(doc).hasAnswer
  );

/**
 * Assessment-only fallback for the rare case where a legacy component's
 * React/iframe bridge failed to expose an answer through getAnswer().
 *
 * This deliberately does NOT replace the individual question handlers. It
 * only reconstructs the simple DOM-backed formats whose answer keys are
 * explicitly embedded in the rendered assessment HTML (currently L and D).
 */
const buildAssessmentDomFallbackAnswer = (
  questionType: string,
  existingAnswer: AssessmentAnswerData | null,
  apiCorrectAnswer: string | null | undefined
): AssessmentAnswerData | null => {
  const documents = getAssessmentQuestionDocuments();
  if (documents.length === 0) return existingAnswer;

  const doc = documents.find((candidate) => {
    const snapshot = readRenderedAssessmentAnswer(candidate);
    return snapshot.hasAnswer;
  });
  if (!doc) return existingAnswer;

  const snapshot = readRenderedAssessmentAnswer(doc);
  if (!snapshot.hasAnswer) return existingAnswer;

  const normalizedType = String(questionType || '').toUpperCase();
  const studentValues = snapshot.values;
  const studentOrder = snapshot.orderValues;

  let fallback: AssessmentAnswerData | null = null;

  // L = ordering / spelling-style question. The backend injects answer-key
  // as a JSON array containing the correct order.
  if (normalizedType === 'L') {
    const key = doc.getElementById('answer-key') as HTMLInputElement | null;
    let correctOrder: string[] = [];

    if (key?.value) {
      try {
        const parsed = JSON.parse(key.value);
        if (Array.isArray(parsed)) {
          correctOrder = parsed.map(normalizeAssessmentValue).filter(Boolean);
        }
      } catch {
        // Fall through to API answer below.
      }
    }

    if (correctOrder.length === 0 && apiCorrectAnswer) {
      try {
        const parsed = JSON.parse(String(apiCorrectAnswer));
        if (Array.isArray(parsed)) {
          correctOrder = parsed.map(normalizeAssessmentValue).filter(Boolean);
        }
      } catch {
        const scalar = normalizeAssessmentValue(apiCorrectAnswer);
        if (scalar) correctOrder = [scalar];
      }
    }

    if (correctOrder.length > 0) {
      const isCorrect =
        studentOrder.length === correctOrder.length &&
        studentOrder.every(
          (value, index) =>
            normalizeAssessmentValue(value).toLowerCase() ===
            normalizeAssessmentValue(correctOrder[index]).toLowerCase()
        );

      const entered = studentOrder.join('|');
      fallback = {
        marks: isCorrect ? 1 : 0,
        noOfOptions: correctOrder.length,
        enteredCorrectAnswer: isCorrect ? entered : '',
        enteredWrongAnswer: isCorrect ? '' : entered,
        enteredBlankAnswer: '',
        scoringMethod: existingAnswer?.scoringMethod ?? null,
        optOrder: existingAnswer?.optOrder || '',
      };
    }
  }

  // D = dropdown question. The backend injects correct-answers as a JSON
  // object keyed by dropdown_N.
  if (normalizedType === 'D') {
    const key = doc.getElementById('correct-answers') as HTMLInputElement | null;
    let correctAnswers: Record<string, string> = {};

    if (key?.value) {
      try {
        const parsed = JSON.parse(key.value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          correctAnswers = Object.fromEntries(
            Object.entries(parsed).map(([k, v]) => [k, normalizeAssessmentValue(v)])
          );
        }
      } catch {
        // Fall through to API answer below.
      }
    }

    if (Object.keys(correctAnswers).length === 0 && apiCorrectAnswer) {
      try {
        const parsed = JSON.parse(String(apiCorrectAnswer));
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          correctAnswers = Object.fromEntries(
            Object.entries(parsed).map(([k, v]) => [k, normalizeAssessmentValue(v)])
          );
        }
      } catch {
        // No structured answer available.
      }
    }

    const dropdowns = Array.from(
      doc.querySelectorAll<HTMLSelectElement>('select[id^="dropdown_"]')
    );

    if (dropdowns.length > 0 && Object.keys(correctAnswers).length > 0) {
      let allAnswered = true;
      let allCorrect = true;
      const entered: string[] = [];

      dropdowns.forEach((select) => {
        const value = normalizeAssessmentValue(select.value);
        if (!value) allAnswered = false;
        entered.push(value);

        const expected = normalizeAssessmentValue(correctAnswers[select.id]);
        if (!expected || value.toLowerCase() !== expected.toLowerCase()) {
          allCorrect = false;
        }
      });

      if (allAnswered) {
        const enteredText = entered.join('|');
        fallback = {
          marks: allCorrect ? 1 : 0,
          noOfOptions: dropdowns.length,
          enteredCorrectAnswer: allCorrect ? enteredText : '',
          enteredWrongAnswer: allCorrect ? '' : enteredText,
          enteredBlankAnswer: '',
          scoringMethod: existingAnswer?.scoringMethod ?? null,
          optOrder: existingAnswer?.optOrder || '',
        };
      }
    }
  }

  if (fallback) return fallback;

  // Generic scalar fallback for O/O_IMAGE/B/W/F/N and other simple
  // single-answer types. Only use this when the API supplied a scalar answer
  // key and the student supplied exactly one value, so we do not guess the
  // scoring rules of complex question types.
  if (studentValues.length > 0 && apiCorrectAnswer) {
    const rawCorrect = String(apiCorrectAnswer).trim();
    const looksStructured =
      (rawCorrect.startsWith('{') && rawCorrect.endsWith('}')) ||
      (rawCorrect.startsWith('[') && rawCorrect.endsWith(']'));

    if (!looksStructured) {
      const entered =
        studentValues.length === 1
          ? studentValues[0]
          : studentValues.join('|');
      const normalizedCorrect = normalizeAssessmentValue(rawCorrect);

      const isCorrect =
        normalizeAssessmentValue(entered).toLowerCase() ===
        normalizedCorrect.toLowerCase();

      fallback = {
        marks: isCorrect ? 1 : 0,
        noOfOptions: Math.max(1, studentValues.length),
        enteredCorrectAnswer: isCorrect ? entered : '',
        enteredWrongAnswer: isCorrect ? '' : entered,
        enteredBlankAnswer: '',
        scoringMethod: existingAnswer?.scoringMethod ?? null,
        optOrder: existingAnswer?.optOrder || '',
      };

      return fallback;
    }
  }

  // For every other type, preserve the question component's scoring. If its
  // answer strings were lost but the DOM clearly contains a student answer,
  // restore the answer text without inventing a new score.
  if (existingAnswer && hasRenderedAssessmentAnswer()) {
    const entered = studentValues.join('|');
    if (entered) {
      return {
        ...existingAnswer,
        enteredCorrectAnswer:
          existingAnswer.marks === 1
            ? (existingAnswer.enteredCorrectAnswer || entered)
            : '',
        enteredWrongAnswer:
          existingAnswer.marks === 0
            ? (existingAnswer.enteredWrongAnswer || entered)
            : '',
      };
    }
  }

  return null;
};

const AssessmentEC: React.FC = () => {

  const navigate = useNavigate();
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [showTestComplete, setShowTestComplete] = useState(false);
  const questionRef = useRef<any>(null);
  const initializedRef = useRef(false);
  const submittingRef = useRef(false);
  const lastAlertRef = useRef<{ message: string; time: number } | null>(null);
  const alertLockRef = useRef(false);
  const alertUnlockTimerRef = useRef<number | null>(null);
  const [searchParams] = useSearchParams();

  const reportNo = searchParams.get('ReportNo') || '';
  const calledFrom = searchParams.get('CalledFrom') || '';

  const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

  const sessionData: SessionData = {
    school_code: sessionStorage.getItem('SchoolCode') || '',
    family_code: sessionStorage.getItem('FamilyCode') || '',
    login: sessionStorage.getItem('Login') || '',
    report_no: reportNo,
  };

  const initializeAssessment = useCallback(async () => {
    try {
      setShowMessage(true);
      setLoading(true);
      const response = await fetch(`${apiUrl}assessmentEC/initialize/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionData),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server returned ${response.status}: ${text}`);
      }
      const data = await response.json();
      if (data.success) {
        setAssessmentData(data);
        if (data.question_data?.complete) {
          setShowTestComplete(true);
        }
      } else if (data.error) {
        alert(data.error);
      } else {
        alert('Unexpected response from server.');
      }
    } catch (error) {
      // console.error('Error initializing assessment:', error);
      alert('Error loading assessment. Please try again.');
    } finally {
      setShowMessage(false);
      setLoading(false);
    }
  }, [
    apiUrl,
    sessionData.family_code,
    sessionData.login,
    sessionData.school_code,
    sessionData.report_no,
  ]);

  useEffect(() => {
    if (initializedRef.current) return;

    if (!sessionData.school_code || !sessionData.family_code || !sessionData.login) {
      alert('Session expired. Please login again.');
      window.location.href = '/login';
      return;
    }

    initializedRef.current = true;
    initializeAssessment();
  }, [
    initializeAssessment,
    sessionData.family_code,
    sessionData.login,
    sessionData.school_code,
  ]);

  useEffect(() => {
    if (!showTestComplete) return;

    // VB EAssessment.aspx.vb redirects directly to
    // GenerateECReportAndLP.aspx after completion.
    // React owns the navigation, so no extra latest-report API
    // round trip is required here.
    const queryString =
      `?ReportNo=${encodeURIComponent(reportNo)}` +
      `&CalledFrom=${encodeURIComponent(calledFrom)}`;

    navigate(
      `/EnglishCheck/Assessment/GenerateECReportAndLP${queryString}`
    );
  }, [showTestComplete, reportNo, calledFrom, navigate]);


  const handleOkClick = async () => {
    // FIX: guard set immediately, before any async work.
    //
    // Previously submittingRef was only set to true right before the
    // fetch() call - i.e. AFTER the whole answer-detection retry loop
    // below, which awaits up to ~200ms and shows no loading indicator
    // in that window. A second (or third, or fourth) click during that
    // window was NOT blocked, so each click ran its own independent
    // copy of this function, and each one could independently reach
    // its own alert('Please answer the question.'). Since alert() is
    // blocking, those queued up and appeared one after another - that
    // was the "alert shows 4 times" bug. Setting the guard here, and
    // clearing it in a single `finally` covering every exit path
    // (including the "no answer" case), makes any click that lands
    // during that window a no-op instead of a parallel run.
    if (submittingRef.current) return;
    if (!assessmentData || !questionRef.current) return;
    submittingRef.current = true;

    try {
      let answerData: AssessmentAnswerData | null = null;

      // First use the question component's own validation/scoring.
      try {
        answerData = questionRef.current.getAnswer();
      } catch (error) {
        console.warn('Assessment getAnswer failed; using DOM recovery.', error);
      }

      /*
       * Legacy EC questions communicate through iframe state/postMessage.
       * Typing/clicking can update the iframe before the React state in the
       * question component has caught up. Retry a few times instead of treating
       * that temporary state gap as an unanswered question.
       */
      if (!hasRecordedAssessmentAnswer(answerData)) {
        for (const delay of [0, 50, 150]) {
          if (delay > 0) {
            await new Promise<void>((resolve) => window.setTimeout(resolve, delay));
          }

          // Do not dispatch synthetic input/change events here.
          // The question components already own their event/validation logic.
          // Synthetic events were causing their alerts to fire repeatedly.

          try {
            const retryAnswer = questionRef.current.getAnswer();
            if (retryAnswer) answerData = retryAnswer;
          } catch (error) {
            console.warn('Assessment getAnswer retry failed.', error);
          }

          if (hasRecordedAssessmentAnswer(answerData)) break;
        }
      }

      /*
       * If the component still did not expose its answer, recover it at the
       * AssessmentEC level from the rendered question. This is intentionally
       * Assessment-only, so shared B/O/C/M/W/I/F/L/D/N handlers are untouched.
       */
      if (!hasRecordedAssessmentAnswer(answerData) && hasRenderedAssessmentAnswer()) {
        answerData = buildAssessmentDomFallbackAnswer(
          questionView?.finalQnType || assessmentData.question_data.question_type,
          answerData,
          assessmentData.question_data.correct_answer
        );
      }

      // Never submit an empty answer. This is the only final gate before the API.
      if (!answerData || !hasRecordedAssessmentAnswer(answerData)) {
        alert('Please answer the question.');
        return; // the `finally` below resets submittingRef/loading either way.
      }

      setShowMessage(true);
      setLoading(true);

      const result = answerData.marks === 1 ? 'P' : 'F';

      const response = await fetch(
        `${apiUrl}assessmentEC/submit-answer/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            school_code: sessionData.school_code,
            family_code: sessionData.family_code,
            login: sessionData.login,
            report_no: sessionData.report_no,
            strand: assessmentData.question_data.strand,
            sub_strand: assessmentData.question_data.sub_strand,
            year: assessmentData.question_data.year,
            topic: assessmentData.question_data.topic,
            question_no: assessmentData.question_data.question_no,
            result,
            correct_ans:
              answerData.enteredCorrectAnswer || '',
            wrong_ans:
              answerData.enteredWrongAnswer || '',
            blank_ans:
              answerData.enteredBlankAnswer || '',
            opt_order:
              answerData.optOrder || '',
            ec_curriculum:
              assessmentData.ec_curriculum,
            study_year:
              assessmentData.study_year,
            lowest_year:
              assessmentData.lowest_year,
            highest_year:
              assessmentData.highest_year,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(
          `Server returned ${response.status}: ${text}`
        );
      }

      const data = await response.json();

      if (data.success) {
        if (data.question_data?.complete) {
          setShowTestComplete(true);
        } else {
          setAssessmentData(prev =>
            prev
              ? {
                ...prev,
                question_data: data.question_data,
              }
              : null
          );
        }
      } else {
        alert(
          data.error ||
          'Error submitting answer. Please try again.'
        );
      }
    } catch (err) {
      console.error(err);
      alert(
        'Error submitting answer. Please try again.'
      );
    } finally {
      submittingRef.current = false;
      setShowMessage(false);
      setLoading(false);
    }
  };


  const handleSkipClick = async () => {
    if (submittingRef.current) return;
    if (!assessmentData) return;

    submittingRef.current = true;
    setShowMessage(true);
    setLoading(true);

    try {
      const response = await fetch(
        `${apiUrl}assessmentEC/submit-answer/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            school_code: sessionData.school_code,
            family_code: sessionData.family_code,
            login: sessionData.login,
            report_no: sessionData.report_no,
            strand: assessmentData.question_data.strand,
            sub_strand: assessmentData.question_data.sub_strand,
            year: assessmentData.question_data.year,
            topic: assessmentData.question_data.topic,
            question_no: assessmentData.question_data.question_no,
            result: 'S',
            correct_ans: '',
            wrong_ans: '',
            blank_ans: '',
            opt_order: '',
            ec_curriculum:
              assessmentData.ec_curriculum,
            study_year:
              assessmentData.study_year,
            lowest_year:
              assessmentData.lowest_year,
            highest_year:
              assessmentData.highest_year,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(
          `Server returned ${response.status}: ${text}`
        );
      }

      const data = await response.json();

      if (data.success) {
        if (data.question_data?.complete) {
          setShowTestComplete(true);
        } else {
          setAssessmentData(prev =>
            prev
              ? {
                ...prev,
                question_data: data.question_data,
              }
              : null
          );
        }
      } else {
        alert(
          data.error ||
          'Error skipping question. Please try again.'
        );
      }
    } catch (err) {
      console.error(err);
      alert(
        'Error skipping question. Please try again.'
      );
    } finally {
      submittingRef.current = false;
      setShowMessage(false);
      setLoading(false);
    }
  };


  const handleLogout = () => {
    window.location.href = '/STUDENT/MSR/StudyRoom?Subject=English&lastPage=Assessment';
  };

  const showAlert = useCallback((message: string) => {
    const normalizedMessage = String(message ?? '').trim();
    if (!normalizedMessage) return;

    const now = Date.now();
    const lastAlert = lastAlertRef.current;

    // HARD duplicate protection.
    //
    // A single user action can reach the same question handler multiple
    // times (React render/effect + legacy iframe handler + child callback).
    // Do not allow another native alert while one alert is active, and also
    // suppress repeated messages for a short settling window after it closes.
    if (alertLockRef.current) {
      return;
    }

    if (
      lastAlert &&
      lastAlert.message === normalizedMessage &&
      now - lastAlert.time < 1500
    ) {
      return;
    }

    alertLockRef.current = true;
    lastAlertRef.current = {
      message: normalizedMessage,
      time: now,
    };

    try {
      alert(normalizedMessage);
    } finally {
      // Keep the gate closed briefly after alert() returns because legacy
      // handlers may continue executing immediately after the blocking alert.
      if (alertUnlockTimerRef.current !== null) {
        window.clearTimeout(alertUnlockTimerRef.current);
      }

      alertUnlockTimerRef.current = window.setTimeout(() => {
        alertLockRef.current = false;
        alertUnlockTimerRef.current = null;
      }, 1500);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (alertUnlockTimerRef.current !== null) {
        window.clearTimeout(alertUnlockTimerRef.current);
      }
    };
  }, []);

  const questionView = useMemo(() => {
    if (!assessmentData?.question_data) return null;

    const {
      question_type,
      question_html,
      question_content,
      blob_assets_path,
    } = assessmentData.question_data;

    const htmlToRender = question_html || question_content || '';
    let finalQnType = question_type;

    // IMPORTANT: Keep the exact existing question-type logic.
    if (question_type === 'F') {
      finalQnType = 'F';
    } else {
      let hasDividerBar = false;
      let hasNumpad = false;

      if (htmlToRender) {
        const normalizedHtml = htmlToRender.toLowerCase();
        hasDividerBar =
          normalizedHtml.includes('ksd_dividerbar') ||
          !!htmlToRender.match(/id=["']KSD_DIVIDERBAR["']/i);

        hasNumpad =
          htmlToRender.includes('KSD_NUMPAD') ||
          !!htmlToRender.match(/id=["']KSD_NUMPAD["']/i);

        // Keep the existing DOMParser detection exactly; it is now run
        // only when question data/HTML actually changes, not on every
        // parent render caused by loading/message state.
        if (typeof DOMParser !== 'undefined') {
          try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlToRender, 'text/html');

            if (
              doc.getElementById('KSD_DIVIDERBAR') ||
              doc.querySelector('[id*="KSD_DIVIDERBAR"]')
            ) {
              hasDividerBar = true;
            }

            if (doc.getElementById('KSD_NUMPAD')) {
              hasNumpad = true;
            }
          } catch {
            // Preserve existing behavior: fall back to string detection.
          }
        }
      }

      if (hasNumpad && ['F', 'W', 'N'].includes(question_type)) {
        finalQnType = question_type;
      } else if (hasDividerBar) {
        if (question_type === 'C' || question_type === 'L') {
          finalQnType = question_type;
        } else {
          finalQnType = 'O';
        }
      } else if (question_type === 'O') {
        const detectedType = getQuestionTypeFromHtml(htmlToRender, question_type);
        if (detectedType && detectedType !== 'O') {
          finalQnType = detectedType;
        }
      }
    }

    const htmlForRender =
      finalQnType === 'L'
        ? adjustLOptionsPadding(htmlToRender)
        : htmlToRender;

    return {
      finalQnType,
      htmlForRender,
      blobAssetsPath: blob_assets_path,
    };
  }, [assessmentData?.question_data]);

  const commonQuestionProps = useMemo(() => {
    if (!questionView) return null;

    return {
      questionHtml: questionView.htmlForRender,
      currentPage: 1,
      totalPages: 1,
      showAlert,
      correctAudio: [],
      incorrectAudio: [],
      assetsBaseUrl: questionView.blobAssetsPath,
      lessonMeta: null,
    };
  }, [questionView, showAlert]);

  const renderQuestion = useCallback(() => {
    if (!questionView || !commonQuestionProps) return null;

    switch (questionView.finalQnType) {
      case 'B':
        return <BQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'O':
        return <OQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'O_RADIO':
        return <OQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'O_IMAGE':
        return <ImageQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'C':
        return <CQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'M':
        return <MQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'W':
        return <WQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'I':
        return <IQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'F':
        return <FQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'L':
        return <LQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'D':
        return <DQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      case 'N':
        return <NQns sessionId={null} ref={questionRef} {...commonQuestionProps} />;
      default:
        return (
          <div style={{ padding: '50px', textAlign: 'center' }}>
            <h3>Unsupported question type: {questionView.finalQnType}</h3>
            <p>Please contact support.</p>
          </div>
        );
    }
  }, [questionView, commonQuestionProps]);

  const progressValue =
    assessmentData?.question_data?.progress ?? 0;

  const progressWidth =
    `${Math.max(0, Math.min(100, progressValue))}%`;

  useEffect(() => {
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousHtmlBackground = document.documentElement.style.backgroundColor;

    document.body.style.backgroundColor = '#fff';
    document.documentElement.style.backgroundColor = '#fff';

    // Some legacy question components call window.alert() directly rather
    // than using the showAlert prop. Protect those alerts too.
    const originalAlert = window.alert;
    let lastNativeAlertMessage = '';
    let lastNativeAlertTime = 0;
    let nativeAlertLocked = false;
    let nativeUnlockTimer: number | null = null;

    window.alert = (message?: string) => {
      const normalizedMessage = String(message ?? '').trim();
      const now = Date.now();

      if (!normalizedMessage) return;

      if (
        nativeAlertLocked ||
        (
          normalizedMessage === lastNativeAlertMessage &&
          now - lastNativeAlertTime < 1500
        )
      ) {
        return;
      }

      nativeAlertLocked = true;
      lastNativeAlertMessage = normalizedMessage;
      lastNativeAlertTime = now;

      try {
        originalAlert.call(window, normalizedMessage);
      } finally {
        if (nativeUnlockTimer !== null) {
          window.clearTimeout(nativeUnlockTimer);
        }

        nativeUnlockTimer = window.setTimeout(() => {
          nativeAlertLocked = false;
          nativeUnlockTimer = null;
        }, 1500);
      }
    };

    return () => {
      document.body.style.backgroundColor = previousBodyBackground;
      document.documentElement.style.backgroundColor = previousHtmlBackground;

      if (nativeUnlockTimer !== null) {
        window.clearTimeout(nativeUnlockTimer);
      }

      window.alert = originalAlert;
    };
  }, []);

  return (
    <div
      id="bodyContent"
      className="assessment-ec-page"
      style={{
        backgroundColor: '#fff',
        minHeight: '100vh',
      }}
    >
      <div id="Form">
        <div id="divContent">
          <div id="divAssessment" className="divAssessment">
            <div id="divMDButtons">
              <table cellPadding={0} border={0} cellSpacing={0} className="tableT">
                <tbody>
                  <tr
                    style={{
                      backgroundImage: `url(${top_blue})`,
                      backgroundRepeat: 'repeat-x',
                      height: '79px',
                    }}
                  >
                    <td className="TabledataT" style={{ paddingRight: "10px" }}>
                      <img
                        id="imgbtnLogout"
                        src={signout_n}
                        alt="Logout"
                        onMouseOver={(e) => (e.currentTarget.src = signout_d)}
                        onMouseOut={(e) => (e.currentTarget.src = signout_n)}
                        onClick={handleLogout}
                        style={{ cursor: 'pointer', paddingBottom: '9px', paddingLeft: '5px' }}
                      />
                    </td>
                    <td id="tdLoggedName" className="CssHead" style={{ paddingBottom: '13px', paddingRight: '20px' }}>
                      Logged in as {assessmentData?.first_name}
                    </td>
                    <td className="CssHeadT" style={{ paddingBottom: '13px', paddingRight: '10px', color: 'white', fontSize: '23px', paddingTop: '23px' }}>
                      Progress
                    </td>
                    <td className="Tabledata2" style={{ paddingBottom: '13px' }}>
                      <div
                        id="divProgressBarContainerT"
                        style={{
                          border: '2px solid silver',
                          backgroundColor: '#FFF',
                          // width: '400px',
                          // width:"22vw"
                        }}
                      >
                        <div
                          id="divProgressBar"
                          style={{
                            width: progressWidth,
                            height: '30px',
                            backgroundColor: 'rgb(19, 105, 172)',
                            transition: 'width 0.3s ease',

                          }}
                        ></div>
                      </div>
                    </td>
                    <td
                      className="Tabledata3"
                      style={{
                        paddingBottom: '9px',
                        textAlign: 'right',
                        paddingRight: '20px',
                      }}
                    >
                      <img
                        src={skip_n}
                        alt="Skip"
                        className="imgSkip"
                        onMouseOver={(e) => (e.currentTarget.src = skip_d)}
                        onMouseOut={(e) => (e.currentTarget.src = skip_n)}
                        onMouseDown={(e) => (e.currentTarget.src = skip_d)}
                        onClick={handleSkipClick}
                        style={{ cursor: 'pointer' }}
                      />
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <img
                        src={ok_n}
                        alt="OK"
                        className="imgOk"
                        onMouseOver={(e) => (e.currentTarget.src = ok_d)}
                        onMouseOut={(e) => (e.currentTarget.src = ok_n)}
                        onMouseDown={(e) => (e.currentTarget.src = ok_d)}
                        onClick={handleOkClick}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div id="divMDFrm" className="cssIfrm">
              <table style={{ width: '100%', height: '100%' }}>
                <tbody>
                  {renderQuestion()}
                </tbody>
              </table>
            </div>
          </div>
          <div
            id="divOpaque"
            className="divOpaque"
            style={{
              display: showMessage ? 'block' : 'none',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#cccccc',
              opacity: 0.8,
              zIndex: 200,
            }}
          ></div>
          <div
            id="divTestComplete"
            className="divTestComplete"
            style={{
              display: showTestComplete ? 'flex' : 'none',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'white',
              zIndex: 400,
              fontFamily: 'Arial',
              fontSize: '12pt',
              color: '#1760cf',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
          </div>
        </div>
      </div>
      {loading && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}>
          <Loading />
        </div>
      )}
    </div>
  );
};

export default AssessmentEC;
